import { useCallback, useEffect, useRef, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";

const ENDPOINT = "/admin/settings/notifications";

/**
 * Tenant notification preferences (contract: docs/auth-and-admin/notification-settings.md).
 *
 * `settings` shape: { enabled: bool, booking: bool, purchase: bool, messages: bool,
 * recipient_email: string|null }
 * GET on mount; `save()` PUTs the full document. A blank recipient is normalised to
 * null before sending (blank ⇒ owner email default, per the contract).
 */
export default function useNotificationSettings() {
  const t = useT("admin");
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const savedTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(ENDPOINT);
        if (!cancelled) setSettings(data);
      } catch (e) {
        if (cancelled) return;
        const msg = extractApiError(e, t("avail.error"));
        setError(msg);
        toast.error(msg);
      }
    })();
    return () => {
      cancelled = true;
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, [t]);

  const setField = useCallback((field, value) => {
    setSettings((prev) => (prev ? { ...prev, [field]: value } : prev));
  }, []);

  const save = useCallback(async () => {
    if (!settings || busy) return;
    setBusy(true);
    setError("");
    setSaved(false);
    const email = (settings.recipient_email || "").trim();
    const payload = {
      enabled: !!settings.enabled,
      booking: !!settings.booking,
      purchase: !!settings.purchase,
      messages: !!settings.messages,
      recipient_email: email || null,
    };
    try {
      const res = await api.put(ENDPOINT, payload);
      // The endpoint echoes the stored state; fall back to the accepted payload if the
      // response carries no body.
      setSettings(res?.data ?? payload);
      setSaved(true);
      if (savedTimer.current) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      // The only 422 this form can produce is an invalid recipient email
      // (EmailStr validation on the backend) — surface it localised.
      const msg =
        e?.response?.status === 422
          ? t("settings.notif.invalidEmail")
          : extractApiError(e, t("avail.error"));
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }, [settings, busy, t]);

  return { settings, setField, save, busy, error, saved };
}
